import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/table-selection.css';
import { 
  Card, 
  Button, 
  Table, 
  Space, 
  Tag, 
  Typography, 
  Popconfirm,
  Image,
  Tooltip,
  Switch,
  Upload,
  Modal,
  App,
  Checkbox,
  Progress,
  Skeleton,
  Input
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { videoApi } from '../services/videoApi';
import { Video } from '../types/video';

const { Title } = Typography;

const Videos: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<Video[]>([]);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [selectionType, setSelectionType] = useState<'none' | 'current' | 'all'>('none');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState<Set<string>>(new Set());
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState<string>('');
  const { message } = App.useApp();

  // Debounce search keyword
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchKeyword(searchKeyword);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  // Reset to page 1 when search keyword changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchKeyword]);

  const { data: videosData, isLoading } = useQuery(
    ['videos', currentPage, pageSize, debouncedSearchKeyword], 
    async () => {
      try {
        return await videoApi.getPaginated({ 
          page: currentPage, 
          pageSize: pageSize,
          search: debouncedSearchKeyword || undefined
        });
      } catch (error) {
        console.error('Paginated API failed, falling back to getAll:', error);
        // Fallback to old API
        const allVideos = await videoApi.getAll();
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedVideos = allVideos.slice(startIndex, endIndex);
        
        return {
          data: paginatedVideos,
          total: allVideos.length,
          page: currentPage,
          pageSize: pageSize,
          totalPages: Math.ceil(allVideos.length / pageSize)
        };
      }
    },
    {
      onError: (error: any) => {
        console.error('Error fetching videos:', error);
        message.error('Failed to load videos');
      }
    }
  );
  
  const videos = videosData?.data || [];
  const total = videosData?.total || 0;


  const deleteMutation = useMutation(videoApi.delete, {
    onSuccess: () => {
      message.success('Video deleted successfully!');
      queryClient.invalidateQueries('videos');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete video');
    },
  });

  const bulkDeleteMutation = useMutation(videoApi.bulkDelete, {
    onSuccess: (result) => {
      const { deletedCount, notFoundIds } = result;
      if (notFoundIds.length > 0) {
        message.warning(`${deletedCount} videos deleted. ${notFoundIds.length} videos not found.`);
      } else {
        message.success(`${deletedCount} videos deleted successfully!`);
      }
      setSelectedRowKeys([]);
      setSelectedVideos([]);
      queryClient.invalidateQueries('videos');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete videos');
    },
  });

  const updateVideoMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => videoApi.update(id, data),
    {
      onSuccess: () => {
        message.success('Video updated successfully!');
        queryClient.invalidateQueries('videos');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to update video');
      },
    }
  );

  const handleCreate = () => {
    navigate('/videos/new');
  };

  const handleEdit = (video: Video) => {
    setLoadingVideos(prev => new Set(prev).add(video.id));
    // Simulate loading for better UX
    setTimeout(() => {
      setLoadingVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(video.id);
        return newSet;
      });
      navigate(`/videos/${video.id}/edit`);
    }, 300);
  };

  const handleDelete = (id: string) => {
    setLoadingVideos(prev => new Set(prev).add(id));
    deleteMutation.mutate(id, {
      onSettled: () => {
        setLoadingVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select videos to delete');
      return;
    }
    bulkDeleteMutation.mutate(selectedRowKeys as string[]);
  };

  const handleSelectChange = (selectedRowKeys: React.Key[], selectedRows: Video[]) => {
    setSelectedRowKeys(selectedRowKeys);
    setSelectedVideos(selectedRows);
    // Reset selection type when manually selecting individual rows
    setSelectionType('none');
  };

  const handleSelectCurrentPage = () => {
    // Select only current page videos (10 videos per page)
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageVideos = videos.slice(startIndex, endIndex);
    const currentPageKeys = currentPageVideos.map(video => video.id);
    
    setSelectedRowKeys(currentPageKeys);
    setSelectedVideos(currentPageVideos);
    setSelectionType('current');
  };

  const handleSelectAllPages = async () => {
    setIsSelectingAll(true);
    try {
      // Fetch all video IDs from server for true "select all" functionality
      const allVideos = await videoApi.getAll();
      const allVideoKeys = allVideos.map(video => video.id);
      setSelectedRowKeys(allVideoKeys);
      setSelectedVideos(allVideos);
      setSelectionType('all');
      message.success(`Selected all ${allVideos.length} videos`);
    } catch (error) {
      console.error('Failed to fetch all videos:', error);
      message.error('Failed to select all videos');
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleDeselectAll = () => {
    setSelectedRowKeys([]);
    setSelectedVideos([]);
    setSelectionType('none');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Reset selection when changing pages
    setSelectedRowKeys([]);
    setSelectedVideos([]);
    setSelectionType('none');
    // Clear any loading states when changing pages
    setLoadingVideos(new Set());
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectChange,
    onSelectAll: (selected: boolean, selectedRows: Video[], changeRows: Video[]) => {
      // Header checkbox: Select/deselect only current page videos
      if (selected) {
        // Select all current page videos
        const currentPageKeys = videos.map(video => video.id);
        setSelectedRowKeys(currentPageKeys);
        setSelectedVideos(videos);
      } else {
        // Deselect all current page videos
        const currentPageKeys = videos.map(video => video.id);
        const newKeys = selectedRowKeys.filter(key => !currentPageKeys.includes(key as string));
        const newVideos = selectedVideos.filter(video => !currentPageKeys.includes(video.id));
        setSelectedRowKeys(newKeys);
        setSelectedVideos(newVideos);
      }
    },
    onSelect: (record: Video, selected: boolean, selectedRows: Video[]) => {
      if (selected) {
        setSelectedRowKeys([...selectedRowKeys, record.id]);
        setSelectedVideos([...selectedVideos, record]);
      } else {
        setSelectedRowKeys(selectedRowKeys.filter(key => key !== record.id));
        setSelectedVideos(selectedVideos.filter(video => video.id !== record.id));
      }
    },
    selections: [
      {
        key: 'current',
        text: 'Select Current Page',
        onSelect: handleSelectCurrentPage,
      },
      {
        key: 'all',
        text: 'Select All Pages',
        onSelect: handleSelectAllPages,
      },
      {
        key: 'none',
        text: 'Deselect All',
        onSelect: handleDeselectAll,
      },
    ],
    columnTitle: (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        color: 'var(--ant-color-text)'
      }}>
        <span style={{ 
          fontWeight: 500,
          fontSize: '12px',
          color: 'var(--ant-color-text)'
        }}>
          Select
        </span>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '2px' 
        }}>
          <Checkbox 
            onChange={(e) => {
              if (e.target.checked) {
                handleSelectCurrentPage();
              } else {
                handleDeselectAll();
              }
            }}
            checked={selectionType === 'current'}
            style={{ 
              fontSize: '11px',
              color: 'var(--ant-color-text)'
            }}
          >
            <span style={{ 
              fontSize: '11px',
              fontWeight: 500,
              color: 'var(--ant-color-text)'
            }}>
              Page
            </span>
          </Checkbox>
          <Checkbox 
            onChange={async (e) => {
              if (e.target.checked) {
                await handleSelectAllPages();
              } else {
                handleDeselectAll();
              }
            }}
            checked={selectionType === 'all'}
            disabled={isSelectingAll}
            style={{ 
              fontSize: '11px',
              color: isSelectingAll ? 'var(--ant-color-text-disabled)' : 'var(--ant-color-text)'
            }}
          >
            <span style={{ 
              fontSize: '11px',
              fontWeight: 500,
              color: isSelectingAll ? 'var(--ant-color-text-disabled)' : 'var(--ant-color-text)'
            }}>
              {isSelectingAll ? 'Load...' : 'All'}
            </span>
          </Checkbox>
          <Checkbox 
            onChange={(e) => {
              if (e.target.checked) {
                handleDeselectAll();
              } else {
                // If unchecking "None", do nothing (keep current selection)
              }
            }}
            checked={selectionType === 'none'}
            style={{ 
              fontSize: '11px',
              color: 'var(--ant-color-text)'
            }}
          >
            <span style={{ 
              fontSize: '11px',
              fontWeight: 500,
              color: 'var(--ant-color-text)'
            }}>
              None
            </span>
          </Checkbox>
        </div>
      </div>
    ),
  };

  const handleExportCSV = async () => {
    try {
      // Show loading message
      const hideLoading = message.loading('Loading all videos for export...', 0);
      
      // Fetch all videos from API (not just current page)
      const allVideos = await videoApi.getAll();
      
      hideLoading();

      // Create CSV headers (matching import format)
      const headers = [
        'title',
        'videoLink',
        'description',
        'imageUrl',
        'duration',
        'viewCount',
        'category',
        'tags',
        'isPublished',
        'websites'
      ];

      // Convert videos data to CSV format (matching import format)
      const csvData = allVideos.map(video => {
        // Format tags array as comma-separated string
        const tagsString = video.tags?.join(',') || '';
        
        // Format websites array as comma-separated IDs (matching import format)
        const websitesString = video.websites?.join(',') || '';
        
        return [
          `"${video.title || ''}"`,
          `"${video.videoLink || ''}"`,
          `"${video.description || ''}"`,
          `"${video.imageUrl || ''}"`,
          video.duration || 0,
          video.viewCount || 0,
          `"${video.category || ''}"`,
          `"${tagsString}"`,
          video.isPublished ? 'true' : 'false',
          `"${websitesString}"`
        ].join(',');
      });

      // Combine headers and data
      const csvContent = [headers.join(','), ...csvData].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `videos_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Successfully exported ${allVideos.length} videos to CSV!`);
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export videos to CSV');
    }
  };

  const handleTogglePublished = (video: Video) => {
    const newPublishedStatus = !video.isPublished;
    updateVideoMutation.mutate({
      id: video.id,
      data: { isPublished: newPublishedStatus }
    });
  };

  const importMutation = useMutation(videoApi.importVideos, {
    onMutate: () => {
      setIsUploading(true);
      setUploadProgress(0);
    },
    onSuccess: (result) => {
      setUploadProgress(100);
      if (result.errors.length > 0) {
        message.warning(`Import completed with ${result.success} successes and ${result.errors.length} errors`);
        console.log('Import errors:', result.errors);
      } else {
        message.success(`Successfully imported ${result.success} videos!`);
      }
      // Delay table re-render until after progress bar completes
      setTimeout(() => {
        queryClient.invalidateQueries('videos');
        setIsUploading(false);
        setUploadProgress(0);
        setIsImportModalVisible(false);
      }, 1000);
    },
    onError: (error: any) => {
      setIsUploading(false);
      setUploadProgress(0);
      message.error(error.response?.data?.message || 'Failed to import videos');
    },
  });

  const handleImport = (file: File) => {
    importMutation.mutate(file);
    
    // Simulate progress bar
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress < 90) {
        setUploadProgress(Math.min(Math.round(progress), 90));
      } else {
        clearInterval(interval);
        // Ensure progress reaches 100% before API completes
        setTimeout(() => {
          setUploadProgress(100);
        }, 500);
      }
    }, 200);
    
    return false; // Prevent default upload
  };


  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'green', text: 'Active' },
      inactive: { color: 'orange', text: 'Inactive' },
      processing: { color: 'blue', text: 'Processing' },
      error: { color: 'red', text: 'Error' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      // Format as h:m:s (e.g., 1:30:45)
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      // Format as m:s (e.g., 30:45)
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const columns = [
    {
      title: 'Thumb',
      dataIndex: 'imageUrl',
      key: 'thumbnail',
      width: 50,
      render: (imageUrl: string, record: Video) => (
        <div style={{ width: 60, height: 40, borderRadius: 4, overflow: 'hidden' }}>
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={record.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              fallback="/images/video-thumbnail-placeholder.svg"
            />
          ) : (
            <img 
              src="/images/video-thumbnail-placeholder.svg" 
              alt="Video placeholder"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover'
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Video) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.category && <Tag>{record.category}</Tag>}
            {record.duration && <span>{formatDuration(record.duration)}</span>}
          </div>
        </div>
      ),
    },
    {
      title: 'Views',
      dataIndex: 'viewCount',
      key: 'viewCount',
      width: 50,
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 50,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Pub',
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 50,
      render: (isPublished: boolean, record: Video) => (
        <Switch
          checked={isPublished}
          onChange={() => handleTogglePublished(record)}
          loading={updateVideoMutation.isLoading}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 70,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: any, record: Video) => {
        const isVideoLoading = loadingVideos.has(record.id);
        return (
          <Space>
            <Tooltip title="View Video">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => {
                  setLoadingVideos(prev => new Set(prev).add(record.id));
                  setTimeout(() => {
                    setLoadingVideos(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(record.id);
                      return newSet;
                    });
                    navigate(`/videos/${record.id}`);
                  }, 300);
                }}
                loading={isVideoLoading}
              />
            </Tooltip>
            <Tooltip title="Edit Video">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                loading={isVideoLoading}
              />
            </Tooltip>
            <Popconfirm
              title="Are you sure you want to delete this video?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete Video">
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />} 
                  loading={isVideoLoading}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>Videos</Title>
        <Space>
          <Input
            placeholder="Search videos by title, description, category, or tags..."
            prefix={<SearchOutlined />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} selected videos?`}
              description="This action cannot be undone."
              onConfirm={handleBulkDelete}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button 
                danger
                icon={<DeleteOutlined />}
                loading={bulkDeleteMutation.isLoading}
              >
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
          <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Button 
            icon={<UploadOutlined />} 
            onClick={() => setIsImportModalVisible(true)}
            loading={importMutation.isLoading}
          >
            Import Videos
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Video
          </Button>
        </Space>
      </div>

      <Card>
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : (
          <div 
            style={{
              // Custom CSS variables for selected row styling
              '--selected-row-bg': 'rgba(24, 144, 255, 0.12)',
              '--selected-row-hover-bg': 'rgba(24, 144, 255, 0.18)',
              '--selected-row-border': 'rgba(24, 144, 255, 0.25)'
            } as React.CSSProperties}
          >
            <Table
              columns={columns}
              dataSource={videos as Video[]}
              rowKey="id"
              pagination={{ 
                pageSize: pageSize,
                current: currentPage,
                total: total,
                onChange: handlePageChange,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
              }}
              rowSelection={rowSelection}
              rowClassName={(record) => {
                const isSelected = selectedRowKeys.includes(record.id);
                return isSelected ? 'selected-row' : '';
              }}
            />
          </div>
        )}
      </Card>

      <Modal
        title="Import Videos"
        open={isImportModalVisible}
        onCancel={() => setIsImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Upload a CSV file to import multiple videos at once.</p>
          <p><strong>Required fields:</strong> title, videoLink, imageUrl (Thumbnail URL), duration</p>
          <p><strong>Optional fields:</strong> description, viewCount, category, tags, isPublished, websites</p>
        </div>
        
        <Upload.Dragger
          accept=".csv"
          beforeUpload={handleImport}
          showUploadList={false}
          disabled={isUploading}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            {isUploading ? 'Uploading...' : 'Click or drag CSV file to this area to upload'}
          </p>
          <p className="ant-upload-hint">
            Support for CSV files only. Download sample format below.
          </p>
        </Upload.Dragger>

        {isUploading && (
          <div style={{ marginTop: 16 }}>
            <Progress 
              percent={Math.round(uploadProgress)} 
              status={uploadProgress === 100 ? 'success' : 'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
              {uploadProgress < 100 ? 'Processing videos...' : 'Upload completed!'}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button 
            type="link" 
            onClick={() => {
              // Create and download sample CSV
              const sampleData = [
                'title,videoLink,description,imageUrl,duration,viewCount,category,tags,isPublished,websites',
                'Sample Video 1,https://youtube.com/watch?v=sample1,Sample description,https://example.com/thumb1.jpg,180,100,Tutorial,"tutorial,sample",true,',
                'Sample Video 2,https://youtube.com/watch?v=sample2,Another description,https://example.com/thumb2.jpg,240,50,Demo,"demo,example",false,',
                'Sample Video 3,https://youtube.com/watch?v=sample3,Third video,https://example.com/thumb3.jpg,120,200,Review,"review,test",true,'
              ].join('\n');
              
              const blob = new Blob([sampleData], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'videos_import_sample.csv';
              a.click();
              window.URL.revokeObjectURL(url);
            }}
          >
            Download Sample CSV Format
          </Button>
        </div>
      </Modal>

    </div>
  );
};

export default Videos;
